import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  type ChannelMarkupEvaluationContext,
  type ChannelMarkupV2ClientInit,
  type ClientInitConfig,
  evaluateChannelMarkupRound,
  evaluateChannelMarkup as evaluateSelectedChannelMarkup,
  getChannelMarkupConditionConfigByKey,
  selectChannelMarkup,
} from '@luxuryescapes/contract-svc-promo/channelMarkup';
import {
  type ChannelMarkupConditionApplyType,
  type ChannelMarkupConditionType,
  channelMarkupConditionConfig,
} from '@luxuryescapes/contract-svc-promo/channelMarkup/admin/v2';
import type { components, operations } from '@luxuryescapes/contract-svc-promo/generated';

import { type ChannelMarkupV2Internal, createOrUpdateChannelMarkupV2 } from '~/services/PromoService';

import { channelMarkupV2QueryKeys } from './useGetChannelMarkupV2ById';

type Condition = {
  id: string;
  condition_type: ChannelMarkupConditionType;
  condition_apply_type: string;
  condition_value: string;
  specificMarkupIds: Array<string>;
};

type Markup = {
  id: string;
  markup_type: 'fixed' | 'percentage';
  markup_value: string;
  lux_plus_markup_value: string;
  channelMarkupCTAText: string;
  currency: components['schemas']['Promo Currencies'] | '';
};

type TesterValues = {
  context: ChannelMarkupEvaluationContext & {
    siteLocation?: string;
  };
};

type EvaluationMode = 'current' | 'general' | 'two-shot';

type ChannelMarkupV2Payload = operations['createChannelMarkupV2']['requestBody']['content']['application/json'];
type CreateOrUpdateChannelMarkupV2Response =
  | operations['createChannelMarkupV2']['responses']['201']['content']['application/json']
  | operations['updateChannelMarkupV2']['responses']['200']['content']['application/json'];

const CHANNEL_MARKUP_CREATABLE_CONDITION_TYPES = [
  'loyaltyOfferType',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'subscriberTier',
  'userRoleCondition',
] as const;

const CHANNEL_MARKUP_SELECTION_CONDITION_TYPES = new Set(['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm']);

type TesterScenarioResult = {
  context: ChannelMarkupEvaluationContext;
  region: string;
  currency: string;
  mode: EvaluationMode;
  status: ReturnType<typeof evaluateChannelMarkupRound>['status'];
  evaluation: ReturnType<typeof evaluateChannelMarkupRound>;
  conditionEvaluations: Array<{
    id: string;
    conditionType: string;
    conditionValue: string;
    passed: boolean;
    contextValue: string;
  }>;
  presentation: {
    channelMarkupCallToActionText: string | null;
    matchCount: number;
  } | null;
};

type CurrentEvaluatorResult = ReturnType<typeof evaluateChannelMarkupRound>;
type CurrentEvaluatorState = CurrentEvaluatorResult['state'];

type ApplyParams = {
  isEditMode: boolean;
  createPayload: ChannelMarkupV2Payload;
  updatePayload: operations['updateChannelMarkupV2']['requestBody']['content']['application/json'];
};

type ApplyResult = {
  response: CreateOrUpdateChannelMarkupV2Response;
  channelMarkup: ChannelMarkupV2Internal | null;
  warnings: Array<string>;
};

const extractChannelMarkupFromResponse = (
  response: CreateOrUpdateChannelMarkupV2Response,
): ChannelMarkupV2Internal | null => {
  const result = response.result;
  return result ?? null;
};

const toNumberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const trimOrFallback = (value: string | undefined, fallback: string | null = ''): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
};

const toNullableNumber = (value: string | undefined): number | null => {
  if (value === undefined || value.trim() === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatContextValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(', ');
  }

  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return `${value}`;
  }

  return '';
};

const buildConditionEvaluations = (evaluation: CurrentEvaluatorResult) => {
  const evaluations = evaluation.state.candidates.flatMap((candidate) => candidate.evaluations);

  return evaluations.map((condition, index) => ({
    id: `${condition.conditionId}-${index}`,
    conditionType: condition.condition_type,
    conditionValue: condition.condition_value,
    passed: condition.status === 'match',
    contextValue: formatContextValue(condition.context.contextValue),
  }));
};

/**
 * Customer portal resolves a single viable channel markup from session context before it sees an offer.
 * Keep this behaviour separate from the one-round tester so both outcomes can be inspected.
 */
const narrowToFirstViableChannelMarkup = (state: CurrentEvaluatorState): CurrentEvaluatorState => {
  const firstViableCandidate = state.candidates.find((candidate) =>
    candidate.evaluations.every((condition) => {
      const conditionConfig = getChannelMarkupConditionConfigByKey(condition.condition_type);
      return conditionConfig?.category === 'user' ? condition.status === 'match' : true;
    }),
  );

  if (!firstViableCandidate) {
    return state;
  }

  const candidates = state.candidates.filter(
    (candidate) => candidate.channelMarkupId === firstViableCandidate.channelMarkupId,
  );

  return candidates.length === state.candidates.length ? state : { ...state, candidates };
};

const buildCurrentChannelMarkupConfig = (
  channelMarkupId: string,
  conditions: Array<Condition>,
  markups: Array<Markup>,
): ClientInitConfig['channelMarkupConfig'] => ({
  channelMarkupListV1: [],
  channelMarkupListV2: [
    {
      version: 'v2',
      channelMarkupId,
      rules: buildRulesPayload(conditions, markups),
    },
  ],
  globalOfferIdBlockList: [],
});

const createEmptyEvaluation = (
  context: ChannelMarkupEvaluationContext,
  status: 'pending' | 'no-match',
): CurrentEvaluatorResult => {
  const state = {
    version: 'cmEvaluationV1' as const,
    context,
    candidates: [],
    eliminatedCandidates: [],
  };

  return status === 'no-match' ? { status, state, reason: 'no-candidates' } : { status, state };
};

const hasUnsupportedConditionConfig = (conditions: Array<Condition>): boolean =>
  conditions.some((condition) => !isChannelMarkupConditionType(condition.condition_type));

const hasTopLevelUtmSelectionCondition = (config: ClientInitConfig['channelMarkupConfig']): boolean =>
  config.channelMarkupListV2.some((channelMarkup) =>
    channelMarkup.rules.conditions.some(
      (condition) =>
        CHANNEL_MARKUP_SELECTION_CONDITION_TYPES.has(condition.conditionType) &&
        (!condition.specificMarkupIds || condition.specificMarkupIds.length === 0),
    ),
  );

const isCreatableChannelMarkupConditionType = (conditionType: string): conditionType is ChannelMarkupConditionType =>
  CHANNEL_MARKUP_CREATABLE_CONDITION_TYPES.includes(
    conditionType as (typeof CHANNEL_MARKUP_CREATABLE_CONDITION_TYPES)[number],
  );

const buildRulesPayload = (
  conditions: Array<Condition>,
  markups: Array<Markup>,
): ChannelMarkupV2ClientInit['rules'] => ({
  conditions: conditions.flatMap((condition) => {
    const conditionConfig =
      channelMarkupConditionConfig.find((config) => config.key === condition.condition_type) ?? null;

    if (!conditionConfig || !isCreatableChannelMarkupConditionType(conditionConfig.key)) {
      return [];
    }

    const fallbackApplyType = conditionConfig?.conditionApplyTypes?.[0] ?? 'MUST-INCLUDE';
    const conditionType = conditionConfig.key;
    const conditionApplyType = isChannelMarkupConditionApplyType(condition.condition_apply_type)
      ? condition.condition_apply_type
      : fallbackApplyType;

    return {
      conditionId: condition.id,
      specificMarkupIds: condition.specificMarkupIds.length > 0 ? condition.specificMarkupIds : null,
      conditionType,
      conditionApplyType,
      conditionValue: condition.condition_value,
    };
  }),
  markups: markups.map((markup) => ({
    markupId: markup.id,
    markupType: markup.markup_type,
    markupValue: toNumberOrUndefined(markup.markup_value),
    luxPlusMarkupValue: toNumberOrUndefined(markup.lux_plus_markup_value),
    channelMarkupCTAText: markup.channelMarkupCTAText || null,
    ...(markup.currency ? { currency: markup.currency } : {}),
  })),
});

export const normalizeResponseWarnings = (response: unknown): Array<string> => {
  if (!isResponseWithWarnings(response)) {
    return [];
  }

  const warnings = response.result?.warnings ?? response.warnings ?? [];
  return warnings
    .map((warning) => (typeof warning === 'string' ? warning : warning.message))
    .filter((warning): warning is string => Boolean(warning));
};

function isResponseWithWarnings(value: unknown): value is {
  warnings?: Array<{ message?: string } | string>;
  result?: { warnings?: Array<{ message?: string } | string> };
} {
  return typeof value === 'object' && value !== null && ('warnings' in value || 'result' in value);
}

export function useChannelMarkupV2() {
  const queryClient = useQueryClient();

  const evaluateChannelMarkup = useCallback(
    ({
      mode = 'current',
      channelMarkupId,
      conditions,
      markups,
      testerValues,
      clientInitConfig,
    }: {
      mode?: EvaluationMode;
      channelMarkupId: string;
      conditions: Array<Condition>;
      markups: Array<Markup>;
      testerValues: TesterValues;
      clientInitConfig?: ClientInitConfig | null;
    }): TesterScenarioResult => {
      const { context: testerContext } = testerValues;
      const region = trimOrFallback(testerContext.region ?? undefined, 'AU') ?? 'AU';
      const currency = testerContext.currency?.trim() || 'AUD';
      const context: ChannelMarkupEvaluationContext = {
        region,
        currency,
        utmSource: trimOrFallback(testerContext.utmSource ?? undefined, null),
        utmMedium: trimOrFallback(testerContext.utmMedium ?? undefined, null),
        utmCampaign: trimOrFallback(testerContext.utmCampaign ?? undefined, null),
        utmTerm: trimOrFallback(testerContext.utmTerm ?? undefined, null),
        subscriberTier: trimOrFallback(testerContext.subscriberTier ?? undefined, null),
        roles: testerContext.roles ?? [],
        offer: testerContext.offer
          ? {
              version: 'cmOfferV1',
              total: toNullableNumber(String(testerContext.offer.total ?? '')) ?? 0,
              luxPlusPrice: toNullableNumber(String(testerContext.offer.luxPlusPrice ?? '')),
              offerId: trimOrFallback(testerContext.offer.offerId ?? undefined, 'test-offer') ?? 'test-offer',
              loyaltyOfferType:
                trimOrFallback(testerContext.offer.loyaltyOfferType ?? undefined, 'HOTEL_FLASH') ?? 'HOTEL_FLASH',
            }
          : {
              version: 'cmOfferV1',
              total: 0,
              luxPlusPrice: null,
              offerId: 'test-offer',
              loyaltyOfferType: 'HOTEL_FLASH',
            },
      };
      const hasUnsupportedConditions = hasUnsupportedConditionConfig(conditions);

      if (hasUnsupportedConditions) {
        return {
          context,
          region,
          currency,
          mode,
          status: 'pending',
          evaluation: createEmptyEvaluation(context, 'pending'),
          conditionEvaluations: [],
          presentation: null,
        };
      }

      if (mode === 'general') {
        const generalConfig = clientInitConfig?.channelMarkupConfig;

        if (!generalConfig) {
          return {
            context,
            region,
            currency,
            mode,
            status: 'pending',
            evaluation: createEmptyEvaluation(context, 'pending'),
            conditionEvaluations: [],
            presentation: null,
          };
        }

        const input = {
          version: 'cmChannelMarkupInputV1' as const,
          userInfo: {
            version: 'uiv1' as const,
            subscriberTier: context.subscriberTier,
            roles: context.roles,
          },
          pageInfo: {
            utm_source: context.utmSource,
            utm_medium: context.utmMedium,
            utm_campaign: context.utmCampaign,
            utm_term: context.utmTerm,
          },
          offers: [context.offer],
        };
        const selectedChannelMarkup = hasTopLevelUtmSelectionCondition(generalConfig)
          ? selectChannelMarkup(generalConfig, { pageInfo: input.pageInfo })
          : null;

        if (!selectedChannelMarkup) {
          return {
            context,
            region,
            currency,
            mode,
            status: 'pending',
            evaluation: createEmptyEvaluation(context, 'pending'),
            conditionEvaluations: [],
            presentation: null,
          };
        }

        const offerEvaluation = evaluateSelectedChannelMarkup({
          clientInfo: {
            version: 'ciV1',
            region,
            currency,
          },
          selectedChannelMarkup,
          input,
        });
        const evaluation = offerEvaluation.offers[0].evaluation;

        return {
          context,
          region,
          currency,
          mode,
          status: evaluation.status,
          evaluation,
          conditionEvaluations: buildConditionEvaluations(evaluation),
          presentation:
            evaluation.status === 'matched' && evaluation.matches.length > 0
              ? {
                  channelMarkupCallToActionText: evaluation.matches[0].markup.channelMarkupCTAText ?? null,
                  matchCount: evaluation.matches.length,
                }
              : null,
        };
      }

      const channelMarkupConfig = buildCurrentChannelMarkupConfig(channelMarkupId, conditions, markups);
      const roundEvaluation: CurrentEvaluatorResult =
        mode === 'two-shot'
          ? evaluateChannelMarkupRound({
              state: narrowToFirstViableChannelMarkup(
                evaluateChannelMarkupRound({
                  channelMarkupConfig,
                  context: {
                    region,
                    currency,
                    utmSource: context.utmSource,
                    utmMedium: context.utmMedium,
                    utmCampaign: context.utmCampaign,
                    utmTerm: context.utmTerm,
                    subscriberTier: context.subscriberTier,
                    roles: context.roles,
                  },
                }).state,
              ),
              context: { offer: context.offer },
            })
          : evaluateChannelMarkupRound({ channelMarkupConfig, context });

      return {
        context,
        region,
        currency,
        mode,
        status: roundEvaluation.status,
        evaluation: roundEvaluation,
        conditionEvaluations: buildConditionEvaluations(roundEvaluation),
        presentation:
          roundEvaluation.status === 'matched' && roundEvaluation.matches.length > 0
            ? {
                channelMarkupCallToActionText: roundEvaluation.matches[0].markup.channelMarkupCTAText ?? null,
                matchCount: roundEvaluation.matches.length,
              }
            : null,
      };
    },
    [],
  );

  const applyChannelMarkup = useCallback(
    async ({ isEditMode, createPayload, updatePayload }: ApplyParams): Promise<ApplyResult> => {
      const response = await createOrUpdateChannelMarkupV2(isEditMode ? updatePayload : createPayload, isEditMode);
      // Bust cached channel markup detail(s) so a re-open/refresh reflects the saved change immediately.
      await queryClient.invalidateQueries({ queryKey: channelMarkupV2QueryKeys.all });
      return {
        response,
        channelMarkup: extractChannelMarkupFromResponse(response),
        warnings: normalizeResponseWarnings(response),
      };
    },
    [queryClient],
  );

  return {
    evaluateChannelMarkup,
    applyChannelMarkup,
  };
}

function isChannelMarkupConditionApplyType(value: string): value is ChannelMarkupConditionApplyType {
  return channelMarkupConditionConfig.some((config) =>
    config.conditionApplyTypes?.some((conditionApplyType) => conditionApplyType === value),
  );
}

function isChannelMarkupConditionType(value: string): value is ChannelMarkupConditionType {
  return channelMarkupConditionConfig.some((config) => config.key === value);
}
